import interpolateComponents from 'interpolate-components';
import PropTypes from 'prop-types';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { closePopOutPlayerDialog } from 'src/common/actions/dialog';
import { PLAYER_MANAGER_ENABLED } from 'src/common/constants/experiments/config';
import useActions from 'src/common/hooks/useActions';
import useExperiment from 'src/common/hooks/useExperiment';
import { useStores } from 'src/common/providers';
import { selectIsPopoutPlayerDialogOpen } from 'src/common/selectors/dialog';
import { HELP_CHROME80 } from '../../constants/cmsLinks';
import {
  POP_OUT_DIALOG_MESSAGE_LINK,
  POP_OUT_DIALOG_MESSAGE_V2,
} from '../../constants/localizations/player';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import PopOutButton from '../shared/button/PopOutButton';
import CommonDialog from '../shared/dialog/CommonDialog';
import UnderlineLink from '../shared/link/UnderlineLink';
import css from './popout-player-dialog.module.scss';

const buttonDimensions = {
  width: '45',
  height: '45',
};

export function PopoutPlayerDialog() {
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);
  const actions = useActions({ closePopOutPlayerDialog });
  const isPlayerManagerEnabled = useExperiment(PLAYER_MANAGER_ENABLED);
  const { playerStore } = useStores();
  const setPopoutState = playerStore.setPopoutState();
  const isDialogOpen = isPlayerManagerEnabled
    ? playerStore.popout()?.isDialogOpen
    : // biome-ignore lint/correctness/useHookAtTopLevel: this is fine for the PM experiment
      useSelector(selectIsPopoutPlayerDialogOpen);

  if (!isDialogOpen) {
    return null;
  }

  function closeDialog() {
    isPlayerManagerEnabled
      ? setPopoutState({ isDialogOpen: false })
      : actions.closePopOutPlayerDialog();
  }

  return (
    <CommonDialog
      modal
      dialogOpen
      handleDialogClose={closeDialog}
      contentStyle={{
        maxWidth: 510,
        overflow: 'hidden',
      }}
    >
      <h2 className={css.messageContainer} data-testid="messageContainer">
        {interpolateComponents({
          mixedString: getLocalizedText(POP_OUT_DIALOG_MESSAGE_V2),
          components: {
            helplink: (
              <UnderlineLink
                data-testid="helpLink"
                to={HELP_CHROME80}
                target="_blank"
                isNotReactRouterLink
                isDropDown
              >
                {getLocalizedText(POP_OUT_DIALOG_MESSAGE_LINK)}
              </UnderlineLink>
            ),
          },
        })}
      </h2>
      <div className={css.buttonContainer}>
        <PopOutButton
          width={buttonDimensions.width}
          height={buttonDimensions.height}
          onClick={closeDialog}
          dataTestId="popoutPlayerDialogButton"
        />
      </div>
    </CommonDialog>
  );
}

PopoutPlayerDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  actions: PropTypes.object.isRequired,
};
